import * as React from "react"
import { NewTable } from "@tenforce/toolbox-table-react"

import { GlobalState } from "../../../store"
import { useAppDispatch, useAppSelector } from "../../../hooks"

import { fetchItems, PAGE_SIZE } from "../slice"
import { Input } from "@tenforce/toolbox-field-react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Block, ItemBlockPart } from "@tenforce/toolbox-selector-react"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { ViewSettings } from "./ViewSettings"
import { getDefaultColumnWidth } from "../helper"
import { ThumbTable } from ".."
import { Field } from "../../fields/components/Field"
import { Labels } from "../../../util/Translations"

const getLocalStorageConstant = (listId: number): string =>
    `COLUMN_WIDTHS_${listId}`

export const ThumbTableView = (): JSX.Element => {
    const {
        areColumnsLoaded: isLoading,
        columns,
        collection,
        items,
        totalItemsCount,
    } = useAppSelector((state: GlobalState) => state.thumbTableView)

    const { LoadNMoreOf, LoadNMore } = Labels

    const localStorageConstant = getLocalStorageConstant(
        collection.collectionId,
    )
    const [columnsWidths, setColumnsWidth] = React.useState<number[]>([])
    const loadMoreItems = (): void => {
        dispatch(fetchItems(false))
    }
    const updateColumnsWidth = (widths: number[]): void => {
        if (columns.length === 0) {
            return
        }
        setColumnsWidth(widths)
        window.localStorage.removeItem(localStorageConstant)

        const fieldWidths = columns.map(
            (column, index) =>
                ({
                    fieldId: column.fieldId,
                    width: widths[index],
                } as ThumbTable.FieldWidth),
        )
        window.localStorage.setItem(
            localStorageConstant,
            JSON.stringify(JSON.stringify(fieldWidths)),
        )
    }
    const dispatch = useAppDispatch()

    React.useEffect(() => {
        if (!isLoading) {
            const storedColumnWidths =
                localStorage.getItem(localStorageConstant)
            const tableWidth =
                document.getElementById("collectionView")?.clientWidth ?? 1
            const defaultWidth = tableWidth / columns.length
            if (storedColumnWidths != null) {
                const deserializedWidths = JSON.parse(
                    JSON.parse(storedColumnWidths),
                ) as ThumbTable.FieldWidth[]

                const columnWidths = columns.map(
                    (column) =>
                        deserializedWidths.find(
                            (field) => field.fieldId === column.fieldId,
                        )?.width ?? getDefaultColumnWidth(column, defaultWidth),
                )
                updateColumnsWidth(columnWidths)
            } else {
                const columnWidths = columns.map((column) =>
                    getDefaultColumnWidth(column, defaultWidth),
                )
                updateColumnsWidth(columnWidths)
            }
        }
    }, [isLoading])

    const remainingItemsToLoad = totalItemsCount - items.length
    return (
        <div className="tw-p-2 has-global-hidden-actions">
            <header className="el-step-header">
                <h3>{collection.collectionId}</h3>
                <>
                    <Input
                        placeholder={`${Symbols.Search} search ${totalItemsCount} items`}
                        value=""
                        extraClasses="tw-flex-1 tw-w-full toolbox-field__input--fakeInput is-global-hidden-action"
                    />

                    <div className="el-step-header is-global-hidden-action">
                        <Action actionType={ActionType.icon}>
                            {Symbols.Create}
                        </Action>
                        <Action actionType={ActionType.icon}>
                            {Symbols.Link}
                        </Action>
                        <ItemBlockPart isSelected={true}>
                            <Block>
                                <span>{Symbols.Filter}</span>
                            </Block>
                        </ItemBlockPart>
                        <ViewSettings collectionId={collection.collectionId} />
                    </div>
                </>
            </header>
            <NewTable.Table
                isHidden={false}
                tableId="collectionView"
                columnIds={columns.map((x) => x.fieldId)}
                columnsWidth={columnsWidths}
                onColumnsWidthChange={updateColumnsWidth}
            >
                <NewTable.Header>
                    {columns.map((column, index) => {
                        return (
                            <NewTable.ColumnHead
                                key={index}
                                canResize={index != columns.length - 1}
                                columnIndex={index}
                                columnId={column.fieldId}
                                extraClasses="label action text-action"
                            >
                                {column.label}
                            </NewTable.ColumnHead>
                        )
                    })}
                </NewTable.Header>
                <NewTable.Body>
                    {items.map((item, i) => (
                        <NewTable.Row key={i} level={0}>
                            {columns.map((column, j) => {
                                const itemField = item.fields.find(
                                    (field) => field.fieldId === column.fieldId,
                                )

                                return (
                                    <NewTable.DataCell
                                        key={j}
                                        isFirstInRow={j === 0}
                                    >
                                        {itemField ? (
                                            <Field
                                                field={itemField}
                                                isLoading={isLoading}
                                            />
                                        ) : (
                                            <></>
                                        )}
                                    </NewTable.DataCell>
                                )
                            })}
                        </NewTable.Row>
                    ))}
                </NewTable.Body>
            </NewTable.Table>
            {items.length < totalItemsCount ? (
                <article className="tw-w-full">
                    <Action
                        className="toolbox-action NewUI-card tw-w-full theme-note"
                        isLoading={isLoading}
                        onClick={loadMoreItems}
                    >
                        <div className="NewUI-card__block tw-w-full">
                            {remainingItemsToLoad > PAGE_SIZE
                                ? LoadNMoreOf.replace(
                                      "#{0}",
                                      PAGE_SIZE.toString(),
                                  ).replace(
                                      "#{1}",
                                      (
                                          totalItemsCount - items.length
                                      ).toString(),
                                  )
                                : LoadNMore.replace(
                                      "#{0}",
                                      PAGE_SIZE.toString(),
                                  )}
                        </div>
                    </Action>
                </article>
            ) : (
                <></>
            )}
        </div>
    )
}

export default ThumbTableView
